import React, { useEffect, useId, useRef, useState } from 'react';
import { CalendarPlus, ChevronLeft, ChevronRight, RotateCcw, Search, UsersRound, X } from 'lucide-react';
import {
    SmartScheduleCoach,
    SmartSchedulePeriod,
    toSmartScheduleKst,
} from '@/features/smartSchedule/model';
import type {
    SmartScheduleCountBreakdown,
    VisibleSmartScheduleKind,
} from '@/features/smartSchedule/model';
import {
    formatPeriodTitle,
    getDateKey,
    getDefaultCreateTime,
    moveSelectedDay,
    SmartScheduleFilters,
    SmartStatusFilter,
    SmartTypeFilter,
} from './utils';

type ToolbarProps = {
    viewSwitcher?: React.ReactNode;
    period: SmartSchedulePeriod;
    selectedDay: any;
    coaches: SmartScheduleCoach[];
    filters: SmartScheduleFilters;
    filteredCount: number;
    filteredCountBreakdown: SmartScheduleCountBreakdown;
    onFiltersChange: (filters: SmartScheduleFilters) => void;
    onPeriodChange: (period: SmartSchedulePeriod) => void;
    onSelectedDayChange: (date: any) => void;
    onCreateSlot: (slot: { date: string; startTime: string; coachId?: string; coachName?: string }) => void;
    onOpenCoachOrder?: () => void;
};

const PERIODS: Array<{ value: SmartSchedulePeriod; label: string }> = [
    { value: 'month', label: '월' },
    { value: 'week', label: '주' },
    { value: 'day', label: '일' },
];

const TYPES: Array<{ value: SmartTypeFilter; label: string }> = [
    { value: 'all', label: '전체' },
    { value: 'private', label: '개인' },
    { value: 'group', label: '그룹' },
    { value: 'other', label: '기타' },
];

const STATUS_OPTIONS: Array<{ value: SmartStatusFilter; label: string }> = [
    { value: 'all', label: '모든 상태' },
    { value: 'upcoming', label: '예정' },
    { value: 'ongoing', label: '진행중' },
    { value: 'pending', label: '신청 대기' },
    { value: 'completed', label: '완료' },
    { value: 'noshow', label: '노쇼' },
    { value: 'off', label: 'OFF' },
];

const SCHEDULE_COUNT_ITEMS: Array<{ kind: VisibleSmartScheduleKind; label: string }> = [
    { kind: 'private', label: 'PT 수업' },
    { kind: 'group', label: '그룹수업' },
    { kind: 'request', label: '신청 대기' },
    { kind: 'general', label: '일반 일정' },
    { kind: 'opportunity', label: '신규기회' },
];

const SmartScheduleToolbar = ({
    viewSwitcher,
    period,
    selectedDay,
    coaches,
    filters,
    filteredCount,
    filteredCountBreakdown,
    onFiltersChange,
    onPeriodChange,
    onSelectedDayChange,
    onCreateSlot,
    onOpenCoachOrder,
}: ToolbarProps) => {
    const scheduleCountTooltipId = useId();
    const [isScheduleCountTooltipOpen, setIsScheduleCountTooltipOpen] = useState(false);
    const coachFilterRef = useRef<HTMLDetailsElement>(null);
    const allCoachesSelected = filters.coachMode === 'all';
    const selectedCoaches = allCoachesSelected
        ? coaches
        : coaches.filter((coach) => filters.coachIds.includes(coach.id));
    const selectedCoachLabel =
        allCoachesSelected
            ? '모든 강사'
            : selectedCoaches.length === 0
            ? '선택한 강사 없음'
            : selectedCoaches.length === 1
            ? selectedCoaches[0].name
            : `${selectedCoaches[0].name} 외 ${selectedCoaches.length - 1}명`;

    const updateFilter = <Key extends keyof SmartScheduleFilters>(key: Key, value: SmartScheduleFilters[Key]) => {
        onFiltersChange({ ...filters, [key]: value });
    };

    const updateCoachFilter = (coachMode: SmartScheduleFilters['coachMode'], coachIds: string[]) => {
        onFiltersChange({ ...filters, coachMode, coachIds });
    };

    const resetFilters = () => onFiltersChange({ type: 'all', status: 'all', coachMode: 'all', coachIds: [], query: '' });
    const hasActiveFilter =
        filters.type !== 'all' || filters.status !== 'all' || !allCoachesSelected || Boolean(filters.query);
    const visibleScheduleCountItems = SCHEDULE_COUNT_ITEMS.filter(
        ({ kind }) => filteredCountBreakdown[kind] > 0
    );
    // 월간 총계는 달력에 함께 그려지는 앞뒤 달을 빼고 선택한 달만 센다.
    // 칸 합계와 총계가 달라 보이므로 기준을 툴팁에 밝힌다.
    const isMonthlyCount = period === 'month';
    const scheduleCountTitle = isMonthlyCount
        ? `${toSmartScheduleKst(selectedDay).format('M월')} 일정 집계`
        : '일정 집계';

    useEffect(() => {
        const closeCoachMenu = (event: MouseEvent) => {
            if (coachFilterRef.current && !coachFilterRef.current.contains(event.target as Node)) {
                coachFilterRef.current.open = false;
            }
        };
        const closeCoachMenuWithEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || !coachFilterRef.current?.open) return;
            coachFilterRef.current.open = false;
            coachFilterRef.current.querySelector('summary')?.focus();
        };
        document.addEventListener('mousedown', closeCoachMenu);
        document.addEventListener('keydown', closeCoachMenuWithEscape);
        return () => {
            document.removeEventListener('mousedown', closeCoachMenu);
            document.removeEventListener('keydown', closeCoachMenuWithEscape);
        };
    }, []);

    return (
        <header className="smart-schedule__toolbar">
            <div className="smart-schedule__toolbar-primary">
                <div className="smart-schedule__date-navigation">
                    <button
                        type="button"
                        className="smart-schedule__icon-button"
                        aria-label="이전 기간"
                        onClick={() => onSelectedDayChange(moveSelectedDay(selectedDay, period, -1))}
                    >
                        <ChevronLeft size={19} aria-hidden="true" />
                    </button>
                    <h2 aria-live="polite">{formatPeriodTitle(period, selectedDay)}</h2>
                    <button
                        type="button"
                        className="smart-schedule__icon-button"
                        aria-label="다음 기간"
                        onClick={() => onSelectedDayChange(moveSelectedDay(selectedDay, period, 1))}
                    >
                        <ChevronRight size={19} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="smart-schedule__today-button"
                        onClick={() => onSelectedDayChange(toSmartScheduleKst(new Date()))}
                    >
                        오늘
                    </button>
                </div>

                <div className="smart-schedule__primary-actions">
                    <button
                        type="button"
                        className="smart-schedule__create-button"
                        onClick={() =>
                            onCreateSlot({
                                date: getDateKey(selectedDay),
                                startTime: getDefaultCreateTime(selectedDay),
                            })
                        }
                    >
                        <CalendarPlus size={17} aria-hidden="true" />
                        일정 등록
                    </button>

                    <div className="smart-schedule__period-tabs" role="group" aria-label="달력 표시 기간">
                        {PERIODS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={period === option.value ? 'is-active' : ''}
                                aria-pressed={period === option.value}
                                onClick={() => onPeriodChange(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                {viewSwitcher}
            </div>

            <div className="smart-schedule__toolbar-filters">
                <div className="smart-schedule__type-tabs" role="group" aria-label="일정 유형 필터">
                    {TYPES.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={filters.type === option.value ? 'is-active' : ''}
                            aria-pressed={filters.type === option.value}
                            onClick={() => updateFilter('type', option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <details ref={coachFilterRef} className="smart-schedule__coach-filter">
                    <summary aria-label={`강사 필터, ${selectedCoachLabel}`}>
                        <UsersRound size={15} aria-hidden="true" />
                        <span>{selectedCoachLabel}</span>
                        {!allCoachesSelected && <em>{selectedCoaches.length}</em>}
                    </summary>
                    <div className="smart-schedule__coach-menu">
                        <div className="smart-schedule__coach-menu-heading">
                            <strong>강사 선택</strong>
                            <div className="smart-schedule__coach-menu-actions" role="group" aria-label="강사 목록 관리">
                                {onOpenCoachOrder && (
                                    <button
                                        type="button"
                                        disabled={coaches.length === 0}
                                        aria-label="강사 표시 순서 변경"
                                        onClick={() => {
                                            if (coachFilterRef.current) coachFilterRef.current.open = false;
                                            onOpenCoachOrder();
                                        }}
                                    >
                                        순서 변경
                                    </button>
                                )}
                                <button
                                    type="button"
                                    disabled={allCoachesSelected || coaches.length === 0}
                                    onClick={() => updateCoachFilter('all', [])}
                                >
                                    모두 선택
                                </button>
                                <button
                                    type="button"
                                    disabled={!allCoachesSelected && selectedCoaches.length === 0}
                                    onClick={() => updateCoachFilter('custom', [])}
                                >
                                    모두 해제
                                </button>
                            </div>
                        </div>
                        <div className="smart-schedule__coach-options">
                            {coaches.length === 0 ? (
                                <p>표시할 강사가 없습니다.</p>
                            ) : (
                                coaches.map((coach) => (
                                    <label key={coach.id}>
                                        <input
                                            type="checkbox"
                                            checked={allCoachesSelected || filters.coachIds.includes(coach.id)}
                                            onChange={(changeEvent) => {
                                                const currentIds = allCoachesSelected
                                                    ? coaches.map((item) => item.id)
                                                    : filters.coachIds;
                                                const nextIds = changeEvent.target.checked
                                                    ? Array.from(new Set([...currentIds, coach.id]))
                                                    : currentIds.filter((id) => id !== coach.id);
                                                const nextAllSelected = nextIds.length === coaches.length;
                                                updateCoachFilter(
                                                    nextAllSelected ? 'all' : 'custom',
                                                    nextAllSelected ? [] : nextIds
                                                );
                                            }}
                                        />
                                        <span>{coach.name}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                </details>

                <label className="smart-schedule__select-wrap">
                    <span className="smart-schedule__sr-only">상태 필터</span>
                    <select
                        value={filters.status}
                        onChange={(changeEvent) => updateFilter('status', changeEvent.target.value as SmartStatusFilter)}
                    >
                        {STATUS_OPTIONS.filter((option) => period === 'day' || option.value !== 'off').map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <label className="smart-schedule__search">
                    <Search size={16} aria-hidden="true" />
                    <span className="smart-schedule__sr-only">일정 검색</span>
                    <input
                        type="search"
                        value={filters.query}
                        placeholder="회원·수업·강사 검색"
                        onChange={(changeEvent) => updateFilter('query', changeEvent.target.value)}
                    />
                    {filters.query && (
                        <button type="button" aria-label="검색어 지우기" onClick={() => updateFilter('query', '')}>
                            <X size={14} aria-hidden="true" />
                        </button>
                    )}
                </label>

                <div className="smart-schedule__filter-tail">
                    <button
                        type="button"
                        className="smart-schedule__reset-filter"
                        onClick={resetFilters}
                        disabled={!hasActiveFilter}
                        aria-disabled={!hasActiveFilter}
                        aria-label="초기화"
                        title={hasActiveFilter ? '필터 초기화' : '적용된 필터가 없습니다'}
                    >
                        <RotateCcw size={14} aria-hidden="true" />
                        <span className="smart-schedule__reset-filter-label">초기화</span>
                    </button>
                    <span
                        className="smart-schedule__result-count"
                        aria-live="polite"
                        aria-label={`${filteredCount}개 일정`}
                        onMouseEnter={() => setIsScheduleCountTooltipOpen(true)}
                        onMouseLeave={() => setIsScheduleCountTooltipOpen(false)}
                        onFocus={() => setIsScheduleCountTooltipOpen(true)}
                        onBlur={() => setIsScheduleCountTooltipOpen(false)}
                    >
                        <button
                            type="button"
                            className="smart-schedule__result-count-button"
                            aria-label={`${filteredCount}개 일정, 집계 상세`}
                            aria-describedby={scheduleCountTooltipId}
                        >
                            <span className="smart-schedule__result-count-full" aria-hidden="true">
                                {filteredCount}개 일정
                            </span>
                            <span className="smart-schedule__result-count-compact" aria-hidden="true">
                                {filteredCount}건
                            </span>
                        </button>
                        <span
                            id={scheduleCountTooltipId}
                            className={`smart-schedule__result-count-tooltip${
                                isScheduleCountTooltipOpen ? ' is-open' : ''
                            }`}
                            role="tooltip"
                            aria-hidden={!isScheduleCountTooltipOpen}
                        >
                            <strong>{scheduleCountTitle}</strong>
                            {visibleScheduleCountItems.length > 0 ? (
                                <span className="smart-schedule__result-count-list">
                                    {visibleScheduleCountItems.map(({ kind, label }) => (
                                        <span key={kind} className="smart-schedule__result-count-row">
                                            <span>{label}</span>
                                            <b>{filteredCountBreakdown[kind]}건</b>
                                        </span>
                                    ))}
                                </span>
                            ) : (
                                <span className="smart-schedule__result-count-empty">
                                    집계된 일정이 없습니다.
                                </span>
                            )}
                            {isMonthlyCount && (
                                <span className="smart-schedule__result-count-note">
                                    달력에 함께 보이는 앞뒤 달 일정은 제외했습니다.
                                </span>
                            )}
                        </span>
                    </span>
                </div>
            </div>
        </header>
    );
};

export default SmartScheduleToolbar;
